/**
 * Offline Storage Utility
 * Handles storing and syncing service data when offline
 */

const STORAGE_KEY_PREFIX = 'ecrane_offline_service_';
const SYNC_QUEUE_KEY = 'ecrane_sync_queue';

/**
 * Save service data to offline storage
 * @param {Object} serviceData - The service data to save
 * @returns {string} - The storage key for this service
 */
export const saveOfflineService = (serviceData) => {
  const timestamp = Date.now();
  const storageKey = `${STORAGE_KEY_PREFIX}${timestamp}`;
  
  const dataToStore = {
    ...serviceData,
    timestamp,
    synced: false,
  };
  
  try {
    localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    addToSyncQueue(storageKey);
    return storageKey;
  } catch (error) {
    console.error('Error saving offline service:', error);
    throw error;
  }
};

/**
 * Get all unsynced services from offline storage
 * @returns {Array} - Array of unsynced service data
 */
export const getUnsyncedServices = () => {
  const unsynced = [];
  
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && !data.synced) {
          unsynced.push({ key, ...data });
        }
      }
    }
  } catch (error) {
    console.error('Error getting unsynced services:', error);
  }
  
  return unsynced.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Mark a service as synced and remove from storage
 * @param {string} storageKey - The storage key of the service
 */
export const markServiceSynced = (storageKey) => {
  try {
    localStorage.removeItem(storageKey);
    removeFromSyncQueue(storageKey);
  } catch (error) {
    console.error('Error marking service as synced:', error);
  }
};

/**
 * Clear all offline service data (use with caution)
 */
export const clearAllOfflineServices = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    localStorage.removeItem(SYNC_QUEUE_KEY);
  } catch (error) {
    console.error('Error clearing offline services:', error);
  }
};

/**
 * Add a storage key to the sync queue
 * @param {string} storageKey - The storage key to add
 */
const addToSyncQueue = (storageKey) => {
  try {
    const queue = getSyncQueue();
    if (!queue.includes(storageKey)) {
      queue.push(storageKey);
      localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    }
  } catch (error) {
    console.error('Error adding to sync queue:', error);
  }
};

/**
 * Remove a storage key from the sync queue
 * @param {string} storageKey - The storage key to remove
 */
const removeFromSyncQueue = (storageKey) => {
  try {
    const queue = getSyncQueue();
    const filtered = queue.filter(key => key !== storageKey);
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error removing from sync queue:', error);
  }
};

/**
 * Get the current sync queue
 * @returns {Array} - Array of storage keys in the queue
 */
const getSyncQueue = () => {
  try {
    const queue = localStorage.getItem(SYNC_QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch (error) {
    console.error('Error getting sync queue:', error);
    return [];
  }
};

/**
 * Check if the browser is online
 * @returns {boolean} - True if online, false if offline
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Get count of unsynced services
 * @returns {number} - Number of unsynced services
 */
export const getUnsyncedCount = () => {
  return getUnsyncedServices().length;
};
